/**
 * This date library is based on https://github.com/cibernox/ember-power-calendar-moment and https://github.com/freshworks/ember-power-calendar-dayjs
 * but converted to Typescript and maintained by us. We don't want to rely on yet another package that isn't actively updated.
 */
import dayjs, { type OpUnitType, type QUnitType } from 'dayjs';
import type { DurationUnitType } from 'dayjs/plugin/duration';
import type { DateLibrary } from 'ember-power-calendar/utils';

const add: DateLibrary['add'] = (date, quantity, unit) => {
  return dayjs(date)
    .add(quantity, unit as QUnitType)
    .toDate();
};

const withLocale: DateLibrary['withLocale'] = (locale, fn) => {
  let returnValue;
  if (locale) {
    const previousLocale = dayjs.locale();
    dayjs.locale(locale);
    returnValue = fn();
    dayjs.locale(previousLocale);
  } else {
    returnValue = fn();
  }

  return returnValue;
};

const formatDate: DateLibrary['formatDate'] = (date, format, locale = null) => {
  if (locale) {
    return withLocale(locale, () => dayjs(date).format(format)) as string;
  }

  return dayjs(date).format(format);
};

const startOf: DateLibrary['startOf'] = (date, unit) => {
  return dayjs(date)
    .startOf(unit as OpUnitType)
    .toDate();
};

const endOf: DateLibrary['endOf'] = (date, unit) => {
  return dayjs(date)
    .endOf(unit as OpUnitType)
    .toDate();
};

const weekday: DateLibrary['weekday'] = (date) => {
  return dayjs(date).weekday();
};

const isoWeekday: DateLibrary['isoWeekday'] = (date) => {
  return dayjs(date).isoWeekday();
};

const getWeekdaysShort: DateLibrary['getWeekdaysShort'] = () => {
  return dayjs.weekdaysShort();
};

const getWeekdaysMin: DateLibrary['getWeekdaysMin'] = () => {
  return dayjs.weekdaysMin();
};

const getWeekdays: DateLibrary['getWeekdays'] = () => {
  return dayjs.weekdays();
};

const isAfter: DateLibrary['isAfter'] = (date1, date2) => {
  return dayjs(date1).isAfter(dayjs(date2));
};

const isBefore: DateLibrary['isBefore'] = (date1, date2) => {
  return dayjs(date1).isBefore(dayjs(date2));
};

const isSame: DateLibrary['isSame'] = (date1, date2, unit) => {
  return dayjs(date1).isSame(dayjs(date2), unit as OpUnitType);
};

const isBetween: DateLibrary['isBetween'] = (
  date,
  start,
  end,
  unit,
  inclusivity,
) => {
  return dayjs(date).isBetween(
    start,
    end,
    unit as OpUnitType,
    inclusivity as '()' | '[]' | '[)' | '(]',
  );
};

const diff: DateLibrary['diff'] = (date1, date2) => {
  return dayjs(date1).diff(dayjs(date2));
};

const normalizeDate: DateLibrary['normalizeDate'] = (dateOrDayjs) => {
  if (dateOrDayjs === undefined || dateOrDayjs === null || dateOrDayjs === '') {
    return undefined;
  }

  if (dateOrDayjs instanceof Date) {
    return dateOrDayjs;
  }

  if (dayjs.isDayjs(dateOrDayjs)) {
    return dateOrDayjs.toDate();
  }

  return undefined;
};

const normalizeRangeActionValue: DateLibrary['normalizeRangeActionValue'] = (
  val,
) => {
  return {
    date: val.date,
    moment: {
      start: val.date.start ? dayjs(val.date.start) : val.date.start,
      end: val.date.end ? dayjs(val.date.end) : val.date.end,
    },
  };
};

const normalizeMultipleActionValue: DateLibrary['normalizeMultipleActionValue'] =
  (val) => {
    return {
      date: val.date,
      moment: val.date ? val.date.map((e) => dayjs(e)) : val.date,
    };
  };

const normalizeCalendarDay: DateLibrary['normalizeCalendarDay'] = (day) => {
  day.moment = dayjs(day.date);
  return day;
};

const normalizeCalendarValue: DateLibrary['normalizeCalendarValue'] = (
  value,
) => {
  if (value) {
    return {
      date: value.date,
      moment: value.date ? dayjs(value.date) : undefined,
    };
  }

  return { date: undefined, moment: undefined };
};

const normalizeDuration: DateLibrary['normalizeDuration'] = (value) => {
  if (value === null) {
    return null;
  }

  if (dayjs.isDuration(value)) {
    return value.asMilliseconds();
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const match = value.match(/(\d+)(.*)/);
    if (!match) {
      return undefined;
    }
    const [, quantity, units] = match;
    if (!quantity) {
      return undefined;
    }

    const normalizedUnit = (units?.trim() as DurationUnitType) || 'days';
    return dayjs
      .duration(parseInt(quantity, 10), normalizedUnit)
      .asMilliseconds();
  }

  return undefined;
};

const getDefaultLocale: DateLibrary['getDefaultLocale'] = () => {
  return dayjs.locale();
};

const localeStartOfWeek: DateLibrary['localeStartOfWeek'] = (locale) => {
  const now = new Date();
  const day = withLocale(locale, () =>
    // @ts-expect-error - Seems like a bug in the type definitions of DateLibrary, locale has a default value of null
    formatDate(startOf(now, 'week'), 'dddd'),
  ) as ReturnType<typeof formatDate>;
  const idx = (
    withLocale(locale, getWeekdays) as ReturnType<typeof getWeekdays>
  ).indexOf(day);
  return idx >= 0 ? idx : 0;
};

const startOfWeek: DateLibrary['startOfWeek'] = (day, start) => {
  let startOfWeekDay = day;
  while (isoWeekday(startOfWeekDay) % 7 !== start) {
    startOfWeekDay = add(startOfWeekDay, -1, 'day');
  }

  return startOfWeekDay;
};

const endOfWeek: DateLibrary['endOfWeek'] = (day, start) => {
  const eow = (start + 6) % 7;
  let endOfWeekDay = day;
  while (isoWeekday(endOfWeekDay) % 7 !== eow) {
    endOfWeekDay = add(endOfWeekDay, 1, 'day');
  }

  return endOfWeekDay;
};

const dateLibrary: DateLibrary = {
  add,
  formatDate,
  startOf,
  endOf,
  weekday,
  isoWeekday,
  getWeekdaysShort,
  getWeekdaysMin,
  getWeekdays,
  isAfter,
  isBefore,
  isSame,
  isBetween,
  diff,
  normalizeDate,
  normalizeRangeActionValue,
  normalizeMultipleActionValue,
  normalizeCalendarDay,
  withLocale,
  normalizeCalendarValue,
  normalizeDuration,
  getDefaultLocale,
  localeStartOfWeek,
  startOfWeek,
  endOfWeek,
};

export default dateLibrary;
